import React, { FC, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { FiltersRow } from '../filters';
import { ZTooltip } from '../ztooltip';
import { Icon } from '../icon';
import TableRowsIcon from 'src/shared/material_icons/editor/table_rows.svg';
import ViewModuleIcon from 'src/shared/material_icons/action/view_module.svg';
import { SkeletonBlock, SkeletonRow, SkeletonStack, SkeletonTable, SkeletonTableRow } from '../skeleton';

export type ResourceListViewMode = 'cards' | 'table';

export const RESOURCE_LIST_VIEW_MODE_STORAGE_KEY = 'zakeke.resourceList.viewMode';

export interface ResourceListToolbarProps {
    searchValue: string;
    searchPlaceholder: string;
    viewMode: ResourceListViewMode;
    viewModeLabel: string;
    cardsViewLabel: string;
    tableViewLabel: string;
    onSearchChange: (value: string) => void;
    onViewModeChange: (viewMode: ResourceListViewMode) => void;
}

export interface ResourceListProps extends Omit<ResourceListToolbarProps, 'viewMode' | 'onViewModeChange'> {
    children: (viewMode: ResourceListViewMode) => ReactNode;
}

const isResourceListViewMode = (value: string | null): value is ResourceListViewMode => value === 'cards' || value === 'table';

export const getStoredResourceListViewMode = (): ResourceListViewMode => {
    if (typeof window === 'undefined') return 'cards';

    try {
        const storedViewMode = window.localStorage.getItem(RESOURCE_LIST_VIEW_MODE_STORAGE_KEY);
        return isResourceListViewMode(storedViewMode) ? storedViewMode : 'cards';
    } catch {
        return 'cards';
    }
};

const setStoredResourceListViewMode = (viewMode: ResourceListViewMode) => {
    if (typeof window === 'undefined') return;

    try {
        window.localStorage.setItem(RESOURCE_LIST_VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
        return;
    }
};

const Toolbar = styled(FiltersRow)`
    gap: 12px;
    margin-bottom: 20px;
`;

const ViewModeSwitch = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px;
    background: #ececef;
    border-radius: 8px;
`;

const ViewModeButton = styled.button<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: ${(props) => (props.$active ? '#ffffff' : 'transparent')};
    color: ${(props) => (props.$active ? '#09090b' : '#71717a')};
    box-shadow: ${(props) => (props.$active ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none')};
    cursor: pointer;
    transition:
        background 0.15s ease,
        color 0.15s ease,
        box-shadow 0.15s ease;

    &:hover {
        color: #09090b;
    }
`;

const ViewModeIcon = styled(Icon)``;

export interface ResourceListViewModeSwitchProps {
    viewMode: ResourceListViewMode;
    viewModeLabel: string;
    cardsViewLabel: string;
    tableViewLabel: string;
    onViewModeChange: (viewMode: ResourceListViewMode) => void;
}

// Standalone cards/table (grid/list) segmented switch, reusable outside ResourceList (e.g. the store catalog).
export const ResourceListViewModeSwitch: FC<ResourceListViewModeSwitchProps> = ({
    viewMode,
    viewModeLabel,
    cardsViewLabel,
    tableViewLabel,
    onViewModeChange
}) => {
    const viewModeOptions = [
        { mode: 'cards', label: cardsViewLabel, icon: <ViewModuleIcon /> },
        { mode: 'table', label: tableViewLabel, icon: <TableRowsIcon /> }
    ] as const;

    return (
        <ViewModeSwitch role='group' aria-label={viewModeLabel}>
            {viewModeOptions.map((option) => (
                <ZTooltip key={option.mode} title={option.label} position='bottom'>
                    <ViewModeButton
                        type='button'
                        $active={viewMode === option.mode}
                        aria-label={`${viewModeLabel}: ${option.label}`}
                        aria-pressed={viewMode === option.mode}
                        onClick={() => onViewModeChange(option.mode)}
                    >
                        <ViewModeIcon aria-hidden='true'>{option.icon}</ViewModeIcon>
                    </ViewModeButton>
                </ZTooltip>
            ))}
        </ViewModeSwitch>
    );
};

export const ResourceListToolbar: FC<ResourceListToolbarProps> = ({
    searchValue,
    searchPlaceholder,
    viewMode,
    viewModeLabel,
    cardsViewLabel,
    tableViewLabel,
    onSearchChange,
    onViewModeChange
}) => {
    return (
        <Toolbar
            showSearch
            value={searchValue}
            placeholder={searchPlaceholder}
            onSearchChange={onSearchChange}
            right={
                <ResourceListViewModeSwitch
                    viewMode={viewMode}
                    viewModeLabel={viewModeLabel}
                    cardsViewLabel={cardsViewLabel}
                    tableViewLabel={tableViewLabel}
                    onViewModeChange={onViewModeChange}
                />
            }
        />
    );
};

export const ResourceList: FC<ResourceListProps> = ({ children, ...toolbarProps }) => {
    const [viewMode, setViewMode] = useState<ResourceListViewMode>(getStoredResourceListViewMode);

    const handleViewModeChange = (nextViewMode: ResourceListViewMode) => {
        setViewMode(nextViewMode);
        setStoredResourceListViewMode(nextViewMode);
    };

    return (
        <>
            <ResourceListToolbar {...toolbarProps} viewMode={viewMode} onViewModeChange={handleViewModeChange} />
            {children(viewMode)}
        </>
    );
};

export const ResourceCardsList = styled.div`
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 20px;
    padding: 0;

    @media screen and (min-width: 1280px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    @media screen and (min-width: 2200px) {
        grid-template-columns: repeat(6, minmax(0, 1fr));
    }
`;

const ResourceCardContainer = styled.div<{ $clickable?: boolean }>`
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 18px;
    background-color: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.backgroundColor};
    border: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.border};
    border-radius: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.borderRadius};
    padding: 20px;
    cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
    transition:
        box-shadow 0.15s ease,
        border-color 0.15s ease;
    min-height: 120px;

    &:hover {
        box-shadow: ${(props) => (props.$clickable ? '0 2px 8px rgba(0, 0, 0, 0.06)' : 'none')};
    }
`;

export const ResourceCardTitle = styled.span`
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #263238;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ResourceCardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

const ResourceCardSelection = styled.div`
    display: inline-flex;
    align-items: center;
    margin-left: auto;
`;

export interface ResourceCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    $clickable?: boolean;
    title?: ReactNode;
    selection?: ReactNode;
}

const getResourceCardTitle = (title: ReactNode) => {
    if (typeof title === 'string' || typeof title === 'number') return <ResourceCardTitle>{title}</ResourceCardTitle>;

    return title;
};

export const ResourceCard: FC<ResourceCardProps> = ({ children, selection, title, ...rest }) => {
    return (
        <ResourceCardContainer {...rest}>
            {(title != null || selection != null) && (
                <ResourceCardHeader>
                    {title != null && getResourceCardTitle(title)}
                    {selection != null && (
                        <ResourceCardSelection onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                            {selection}
                        </ResourceCardSelection>
                    )}
                </ResourceCardHeader>
            )}
            {children}
        </ResourceCardContainer>
    );
};

export const ResourceCardFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

export const ResourceCardActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
    flex-wrap: wrap;
    margin-left: auto;
`;

const ResourceListSkeletonToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 20px;
    min-width: 0;

    @media (max-width: 700px) {
        align-items: stretch;
        flex-direction: column;
    }
`;

const ResourceListSkeletonCard = styled(ResourceCard)`
    cursor: default;
`;

export interface ResourceListSkeletonProps {
    viewMode?: ResourceListViewMode;
    cardCount?: number;
    rowCount?: number;
    tableColumns?: string[];
}

export const ResourceListSkeleton: FC<ResourceListSkeletonProps> = ({
    viewMode,
    cardCount = 8,
    rowCount = 8,
    tableColumns = ['minmax(180px, 1fr)', '120px', '84px']
}) => {
    const resolvedViewMode = viewMode ?? getStoredResourceListViewMode();
    const tableTemplateColumns = tableColumns.join(' ');

    return (
        <div aria-hidden='true'>
            <ResourceListSkeletonToolbar>
                <SkeletonBlock $width='min(320px, 100%)' $height='38px' $radius='6px' />
                <SkeletonRow $gap='2px'>
                    <SkeletonBlock $width='32px' $height='28px' $radius='6px' />
                    <SkeletonBlock $width='32px' $height='28px' $radius='6px' />
                </SkeletonRow>
            </ResourceListSkeletonToolbar>
            {resolvedViewMode === 'cards' ? (
                <ResourceCardsList>
                    {Array.from({ length: cardCount }).map((_, index) => (
                        <ResourceListSkeletonCard key={index}>
                            <SkeletonStack $gap='14px'>
                                <SkeletonBlock $width={index % 2 === 0 ? '70%' : '52%'} $height='16px' />
                                <SkeletonRow $gap='12px' $justify='space-between'>
                                    <SkeletonBlock $width='96px' $height='22px' $radius='11px' />
                                    <SkeletonRow $gap='8px'>
                                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                                    </SkeletonRow>
                                </SkeletonRow>
                            </SkeletonStack>
                        </ResourceListSkeletonCard>
                    ))}
                </ResourceCardsList>
            ) : (
                <SkeletonTable>
                    <SkeletonTableRow $columns={tableTemplateColumns} $hideLastBorder={false}>
                        {tableColumns.map((_, index) => (
                            <SkeletonBlock key={index} $width={index === tableColumns.length - 1 ? '64px' : '70%'} $height='13px' />
                        ))}
                    </SkeletonTableRow>
                    {Array.from({ length: rowCount }).map((_, rowIndex) => (
                        <SkeletonTableRow key={rowIndex} $columns={tableTemplateColumns}>
                            {tableColumns.map((_, columnIndex) =>
                                columnIndex === tableColumns.length - 1 ? (
                                    <SkeletonRow key={columnIndex} $gap='8px' $justify='flex-end'>
                                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                                    </SkeletonRow>
                                ) : (
                                    <SkeletonBlock
                                        key={columnIndex}
                                        $width={columnIndex === 0 ? (rowIndex % 2 === 0 ? '220px' : '160px') : '96px'}
                                        $height={columnIndex === 0 ? '16px' : '22px'}
                                        $radius={columnIndex === 0 ? '4px' : '11px'}
                                    />
                                )
                            )}
                        </SkeletonTableRow>
                    ))}
                </SkeletonTable>
            )}
        </div>
    );
};

export const ResourceListEmptyResults = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 160px;
    color: #57534e;
    font-size: 13px;
    border: 1px solid #eef1f3;
    border-radius: 8px;
    background: #ffffff;
`;
